/**
 * WebM Seeking Utilities
 * 
 * WebM videos often have seeking issues due to:
 * 1. Missing or sparse keyframes
 * 2. Poor browser support for WebM seeking
 * 3. Metadata positioning issues
 * 4. Buffering problems
 */

/**
 * Checks if a video URL is a WebM file
 * @param {string} url - Video URL
 * @returns {boolean} - True if URL appears to be WebM
 */
export const isWebMVideo = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  // Check file extension
  if (url.toLowerCase().includes('.webm')) return true;
  
  // Check MIME type in URL (some S3 URLs include this)
  if (url.toLowerCase().includes('video/webm')) return true;
  
  return false;
};

/**
 * Enhanced seeking function for WebM videos
 * @param {Object} playerRef - React Player reference
 * @param {number} seekPosition - Position to seek to (0-1)
 * @param {number} duration - Video duration in seconds
 * @param {boolean} isWebM - Whether this is a WebM video
 * @returns {Promise<boolean>} - Success status
 */
export const enhancedSeek = async (playerRef, seekPosition, duration, isWebM = false) => {
  if (!playerRef.current || !duration || duration <= 0) {
    console.warn('🎬 Enhanced seek: Invalid parameters');
    return false;
  }

  try {
    const targetTime = seekPosition * duration;
    console.log(`🎬 Enhanced seek: Seeking to ${targetTime}s (${(seekPosition * 100).toFixed(1)}%) in ${isWebM ? 'WebM' : 'other'} video`);

  if (isWebM) {
      // WebM-specific seeking strategy
      return await webmSeekStrategy(playerRef, seekPosition, targetTime, duration);
    } else {
      // Standard seeking for other formats
      playerRef.current.seekTo(seekPosition, 'fraction');
      return true;
    }
  } catch (error) {
    console.error('🎬 Enhanced seek error:', error);
    return false;
  }
};

/**
 * WebM-specific seeking strategy with enhanced duration handling
 * @param {Object} playerRef - React Player reference
 * @param {number} seekPosition - Position to seek to (0-1)
 * @param {number} targetTime - Target time in seconds
 * @param {number} duration - Video duration in seconds
 * @returns {Promise<boolean>} - Success status
 */
const webmSeekStrategy = async (playerRef, seekPosition, targetTime, duration) => {
  try {
    console.log('🎬 WebM Strategy: Starting comprehensive seek approach');
    
    // Get the internal video element first
    const internalPlayer = playerRef.current.getInternalPlayer();
    
    if (!internalPlayer) {
      console.warn('🎬 WebM Strategy: No internal player found');
      return false;
    }

    // Strategy 1: Force load video metadata and check duration
    console.log('🎬 WebM Strategy 1: Checking and loading metadata');
    
    // If duration is not available or incorrect, try to get it from the video element
    let actualDuration = sanitizeFiniteDuration(duration);
    if (!isFinite(actualDuration) || actualDuration <= 0) {
      // Try video element directly
      const elDur = getFiniteVideoDuration(internalPlayer);
      if (isFinite(elDur) && elDur > 0) {
        actualDuration = elDur;
        console.log(`🎬 Got duration from video element/seekable: ${actualDuration}s`);
      }
    }
    if (!isFinite(actualDuration) || actualDuration <= 0) {
      // Try forcing metadata load
      console.log('🎬 Forcing metadata load...');
      await forceMetadataLoad(internalPlayer);
      const forcedDur = getFiniteVideoDuration(internalPlayer);
      if (isFinite(forcedDur) && forcedDur > 0) {
        actualDuration = forcedDur;
        console.log(`🎬 Duration after metadata load: ${actualDuration}s`);
      }
    }
    if (!isFinite(actualDuration) || actualDuration <= 0) {
      // Parse WebM header via range request to resolve duration
      const src = internalPlayer.currentSrc || internalPlayer.src;
      if (src) {
        console.log('🎬 Attempting to resolve WebM duration from header via HTTP range');
        try {
          const parsed = await resolveWebMDurationFromHeader(src);
          if (isFinite(parsed) && parsed > 0) {
            actualDuration = parsed;
            console.log(`🎬 Resolved WebM header duration: ${actualDuration}s`);
          }
        } catch (e) {
          console.warn('🎬 Failed to resolve WebM header duration:', e);
        }
      }
    }
    if (!isFinite(actualDuration) || actualDuration <= 0) {
      console.warn('🎬 WebM Strategy: Could not determine finite video duration — deferring seek');
      return false;
    }

    // Recalculate target time with actual duration
    const adjustedTargetTime = seekPosition * actualDuration;
    console.log(`🎬 WebM Strategy: Seeking to ${adjustedTargetTime}s of ${actualDuration}s (${(seekPosition * 100).toFixed(1)}%)`);

    // Strategy 2: Multiple seeking approaches
    let seekSuccess = false;

    // Approach A: ReactPlayer seekTo with seconds
    try {
      console.log('🎬 Approach A: ReactPlayer seekTo with seconds');
      playerRef.current.seekTo(adjustedTargetTime, 'seconds');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const currentTime = internalPlayer.currentTime;
      const timeDiff = Math.abs(currentTime - adjustedTargetTime);
      console.log(`🎬 Approach A result: target=${adjustedTargetTime}s, actual=${currentTime}s, diff=${timeDiff}s`);
      
      if (timeDiff < 2) {
        seekSuccess = true;
      }
    } catch (error) {
      console.warn('🎬 Approach A failed:', error);
    }

    // Approach B: Direct video element manipulation if A failed
    if (!seekSuccess) {
      try {
        console.log('🎬 Approach B: Direct video element manipulation');
        seekSuccess = await directVideoSeek(internalPlayer, adjustedTargetTime, actualDuration);
      } catch (error) {
        console.warn('🎬 Approach B failed:', error);
      }
    }

    // Approach C: Pause-seek-play technique if B failed
    if (!seekSuccess) {
      try {
        console.log('🎬 Approach C: Pause-seek-play technique');
        seekSuccess = await pauseSeekPlayTechnique(internalPlayer, adjustedTargetTime, actualDuration);
      } catch (error) {
        console.warn('🎬 Approach C failed:', error);
      }
    }

    // Approach D: Progressive seeking if C failed
    if (!seekSuccess) {
      try {
        console.log('🎬 Approach D: Progressive seeking');
        seekSuccess = await progressiveSeek(internalPlayer, adjustedTargetTime, actualDuration);
      } catch (error) {
        console.warn('🎬 Approach D failed:', error);
      }
    }

    return seekSuccess;
  } catch (error) {
    console.error('🎬 WebM seeking strategy failed:', error);
    return false;
  }
};

/**
 * Force metadata loading for video element
 * @param {HTMLVideoElement} videoElement - Video element
 * @returns {Promise<boolean>} - Success status
 */
const forceMetadataLoad = async (videoElement) => {
  return new Promise((resolve) => {
    if (videoElement.readyState >= 1) {
      // Metadata already loaded
      resolve(true);
      return;
    }

    const onLoadedMetadata = () => {
      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      resolve(true);
    };

    const onError = () => {
      videoElement.removeEventListener('error', onError);
      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      resolve(false);
    };

    videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
    videoElement.addEventListener('error', onError);

    // Set preload to metadata if not already set
    if (videoElement.preload !== 'metadata' && videoElement.preload !== 'auto') {
      videoElement.preload = 'metadata';
    }

    // Force load by setting currentTime to 0
    try {
      videoElement.currentTime = 0;
    } catch (e) {
      // Ignore errors
    }

    // Timeout after 5 seconds
    setTimeout(() => {
      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      videoElement.removeEventListener('error', onError);
      resolve(false);
    }, 5000);
  });
};

/**
 * Pause-seek-play technique for WebM seeking
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {number} targetTime - Target time in seconds
 * @param {number} duration - Video duration in seconds
 * @returns {Promise<boolean>} - Success status
 */
const pauseSeekPlayTechnique = async (videoElement, targetTime, duration) => {
  try {
    const wasPlaying = !videoElement.paused;
    const clampedTime = Math.max(0, Math.min(targetTime, duration - 0.1));
    
    // Pause the video
    videoElement.pause();
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Set currentTime
    videoElement.currentTime = clampedTime;
    
    // Wait for seeking to complete
    await new Promise((resolve) => {
      const onSeeked = () => {
        videoElement.removeEventListener('seeked', onSeeked);
        resolve();
      };
      
      videoElement.addEventListener('seeked', onSeeked);
      
      // Timeout after 2 seconds
      setTimeout(() => {
        videoElement.removeEventListener('seeked', onSeeked);
        resolve();
      }, 2000);
    });
    
    // Resume playing if it was playing before
    if (wasPlaying) {
      await videoElement.play();
    }
    
    // Check if seek was successful
    const timeDiff = Math.abs(videoElement.currentTime - clampedTime);
    return timeDiff < 2;
  } catch (error) {
    console.error('🎬 Pause-seek-play technique failed:', error);
    return false;
  }
};

/**
 * Progressive seeking technique for stubborn WebM files
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {number} targetTime - Target time in seconds
 * @param {number} duration - Video duration in seconds
 * @returns {Promise<boolean>} - Success status
 */
const progressiveSeek = async (videoElement, targetTime, duration) => {
  try {
    const clampedTime = Math.max(0, Math.min(targetTime, duration - 0.1));
    const currentTime = videoElement.currentTime;
    
    // If we're already close, don't bother
    if (Math.abs(currentTime - clampedTime) < 1) {
      return true;
    }
    
    // Calculate step size (seek in smaller chunks)
    const stepSize = Math.abs(clampedTime - currentTime) / 5;
    const steps = 5;
    
    console.log(`🎬 Progressive seek: from ${currentTime}s to ${clampedTime}s in ${steps} steps of ${stepSize}s`);
    
    for (let i = 1; i <= steps; i++) {
      const intermediateTime = currentTime + (stepSize * i * Math.sign(clampedTime - currentTime));
      const actualTarget = i === steps ? clampedTime : intermediateTime;
      
      videoElement.currentTime = actualTarget;
      await new Promise(resolve => setTimeout(resolve, 200));
      
      console.log(`🎬 Progressive seek step ${i}: set to ${actualTarget}s, actual ${videoElement.currentTime}s`);
    }
    
    // Final check
    const timeDiff = Math.abs(videoElement.currentTime - clampedTime);
    return timeDiff < 2;
  } catch (error) {
    console.error('🎬 Progressive seek failed:', error);
    return false;
  }
};

/**
 * Direct video element seeking for WebM
 * @param {HTMLVideoElement} videoElement - Direct video element
 * @param {number} targetTime - Target time in seconds
 * @param {number} duration - Video duration in seconds
 * @returns {Promise<boolean>} - Success status
 */
const directVideoSeek = async (videoElement, targetTime, duration) => {
  if (!videoElement || typeof videoElement.currentTime === 'undefined') {
    return false;
  }

  try {
    // Ensure target time is within bounds
  const finiteDuration = sanitizeFiniteDuration(duration);
  if (!isFinite(finiteDuration) || finiteDuration <= 0) return false;
  const clampedTime = Math.max(0, Math.min(targetTime, finiteDuration - 0.1));
    
    console.log(`🎬 Direct video seek: Setting currentTime to ${clampedTime}s`);
    
    // Set currentTime directly
    videoElement.currentTime = clampedTime;
    
    // For WebM, sometimes we need to pause and play to trigger seeking
    const wasPlaying = !videoElement.paused;
    
    if (wasPlaying) {
      videoElement.pause();
      await new Promise(resolve => setTimeout(resolve, 100));
      videoElement.play();
    }
    
    return true;
  } catch (error) {
    console.error('🎬 Direct video seek failed:', error);
    return false;
  }
};

/**
 * Preload video metadata to improve seeking
 * @param {string} videoUrl - Video URL
 * @returns {Promise<Object>} - Video metadata
 */
export const preloadVideoMetadata = async (videoUrl) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onMetadata);
      video.removeEventListener('error', onError);
      video.src = '';
    };
    
    const onMetadata = () => {
      console.log('🎬 Video metadata loaded:', {
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState
      });
      
      const metadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        readyState: video.readyState,
        canSeek: video.seekable.length > 0
      };
      
      cleanup();
      resolve(metadata);
    };
    
    const onError = (error) => {
      console.warn('🎬 Failed to preload video metadata:', error);
      cleanup();
      resolve({ error: true });
    };
    
    video.addEventListener('loadedmetadata', onMetadata);
    video.addEventListener('error', onError);
    
    // Timeout after 10 seconds
    setTimeout(() => {
      console.warn('🎬 Video metadata preload timeout');
      cleanup();
      resolve({ timeout: true });
    }, 10000);
    
    video.src = videoUrl;
  });
};

/**
 * Enhanced ReactPlayer configuration for WebM videos
 * @param {string} videoUrl - Video URL
 * @returns {Object} - ReactPlayer config
 */
export const getWebMPlayerConfig = (videoUrl) => {
  const isWebM = isWebMVideo(videoUrl);
  
  const baseConfig = {
    file: {
      attributes: {
        controlsList: "nodownload",
        style: { width: "100%", height: "100%", objectFit: "cover" },
        crossOrigin: "anonymous",
        playsInline: true,
      },
      forceVideo: true,
    },
  };

  if (isWebM) {
    // WebM-specific optimizations
    return {
      ...baseConfig,
      file: {
        ...baseConfig.file,
        attributes: {
          ...baseConfig.file.attributes,
          // WebM-specific attributes
          preload: "metadata", // Load metadata for better seeking
          muted: true, // Start muted to avoid autoplay issues
        },
        // Force MP4 fallback if available
        forceHLS: false,
        forceDASH: false,
      },
    };
  }

  return baseConfig;
};

/**
 * Monitor seeking progress and provide feedback
 * @param {Object} playerRef - React Player reference
 * @param {number} targetPosition - Target position (0-1)
 * @param {Function} onSeekComplete - Callback when seeking completes
 * @param {Function} onSeekProgress - Callback for seek progress updates
 */
export const monitorSeeking = (playerRef, targetPosition, onSeekComplete, onSeekProgress) => {
  if (!playerRef.current) return;

  let checkCount = 0;
  const maxChecks = 20; // Maximum checks (4 seconds at 200ms intervals)
  
  const checkSeekProgress = () => {
    if (!playerRef.current) {
      onSeekComplete(false);
      return;
    }

    try {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (internalPlayer && internalPlayer.currentTime !== undefined) {
        const currentTime = internalPlayer.currentTime;
        let duration = sanitizeFiniteDuration(internalPlayer.duration);
        if (!isFinite(duration) || duration <= 0) {
          const seekableDur = getFiniteVideoDuration(internalPlayer);
          if (isFinite(seekableDur) && seekableDur > 0) duration = seekableDur;
        }
        const currentPosition = duration > 0 ? currentTime / duration : 0;
        const positionDiff = Math.abs(currentPosition - targetPosition);
        
        console.log(`🎬 Seek progress: target=${(targetPosition * 100).toFixed(1)}%, current=${(currentPosition * 100).toFixed(1)}%, diff=${(positionDiff * 100).toFixed(1)}%`);
        
        if (onSeekProgress) {
          onSeekProgress(currentPosition, targetPosition, positionDiff);
        }
        
        // Success if we're within 1% of target position
        if (positionDiff < 0.01) {
          console.log('🎬 Seek completed successfully');
          onSeekComplete(true);
          return;
        }
      }
      
      checkCount++;
      if (checkCount >= maxChecks) {
        console.warn('🎬 Seek monitoring timeout');
        onSeekComplete(false);
        return;
      }
      
      // Continue checking
      setTimeout(checkSeekProgress, 200);
    } catch (error) {
      console.error('🎬 Seek monitoring error:', error);
      onSeekComplete(false);
    }
  };
  
  // Start monitoring after a small delay
  setTimeout(checkSeekProgress, 100);
};

/**
 * Get video format information
 * @param {string} videoUrl - Video URL
 * @returns {Object} - Format information
 */
export const getVideoFormatInfo = (videoUrl) => {
  if (!videoUrl) return { format: 'unknown', isWebM: false };
  
  const url = videoUrl.toLowerCase();
  
  if (url.includes('.webm')) {
    return { format: 'webm', isWebM: true, container: 'webm' };
  } else if (url.includes('.mp4')) {
    return { format: 'mp4', isWebM: false, container: 'mp4' };
  } else if (url.includes('.mkv')) {
    return { format: 'mkv', isWebM: false, container: 'matroska' };
  } else if (url.includes('.mov')) {
    return { format: 'mov', isWebM: false, container: 'quicktime' };
  }
  
  return { format: 'unknown', isWebM: false, container: 'unknown' };
};

const webmSeekingUtils = {
  isWebMVideo,
  enhancedSeek,
  preloadVideoMetadata,
  getWebMPlayerConfig,
  monitorSeeking,
  getVideoFormatInfo
};

export default webmSeekingUtils;

/**
 * Helpers — duration resolution and EBML parsing for WebM
 */

function sanitizeFiniteDuration(value) {
  if (typeof value !== 'number') return NaN;
  if (!isFinite(value)) return NaN;
  if (value <= 0) return NaN;
  return value;
}

function getFiniteVideoDuration(video) {
  if (!video) return NaN;
  const dur = sanitizeFiniteDuration(video.duration);
  if (isFinite(dur)) return dur;
  // Try seekable ranges
  try {
    const seekable = video.seekable;
    if (seekable && seekable.length > 0) {
      const end = seekable.end(seekable.length - 1);
      const finiteEnd = sanitizeFiniteDuration(end);
      if (isFinite(finiteEnd)) return finiteEnd;
    }
  } catch (_) {}
  // Try buffered ranges as a last resort
  try {
    const buffered = video.buffered;
    if (buffered && buffered.length > 0) {
      const end = buffered.end(buffered.length - 1);
      const finiteEnd = sanitizeFiniteDuration(end);
      if (isFinite(finiteEnd)) return finiteEnd;
    }
  } catch (_) {}
  return NaN;
}

async function resolveWebMDurationFromHeader(url) {
  // Fetch first chunk to find Segment Info (contains TimecodeScale and Duration)
  const head = await fetchRange(url, 0, 512 * 1024 - 1);
  if (!head) return NaN;
  const view = new DataView(head);
  const { durationValue, timecodeScale } = parseWebMInfo(view);
  if (isFinite(durationValue)) {
    const scale = isFinite(timecodeScale) && timecodeScale > 0 ? timecodeScale : 1000000; // default 1ms
    // Duration seconds = (durationValue * timecodeScale) / 1e9
    const seconds = (durationValue * scale) / 1e9;
    return seconds > 0 && isFinite(seconds) ? seconds : NaN;
  }
  return NaN;
}

async function fetchRange(url, start, end) {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Range: `bytes=${start}-${end}`,
      },
      mode: 'cors',
      credentials: 'omit',
    });
    if (!res.ok && res.status !== 206 && res.status !== 200) {
      console.warn('🎬 Range request failed:', res.status, res.statusText);
      return null;
    }
    const buf = await res.arrayBuffer();
    return buf;
  } catch (e) {
    console.warn('🎬 Range fetch error:', e);
    return null;
  }
}

function parseWebMInfo(view) {
  // Scan for TimecodeScale (0x2AD7B1) and Duration (0x4489)
  const len = view.byteLength;
  let durationValue = NaN;
  let timecodeScale = 1000000; // default per Matroska spec (1ms)
  for (let i = 0; i < len - 4; i++) {
    const b0 = view.getUint8(i);
    // Duration ID 0x44 0x89
    if (b0 === 0x44 && view.getUint8(i + 1) === 0x89) {
      // Read VINT size to know data length
      const { value: dataSize, length: sizeLen } = readVint(view, i + 2);
      if (dataSize > 0 && dataSize <= 8 && sizeLen > 0) {
        const dataOffset = i + 2 + sizeLen;
        try {
          if (dataSize === 4) {
            durationValue = view.getFloat32(dataOffset, false);
          } else if (dataSize === 8) {
            durationValue = view.getFloat64(dataOffset, false);
          } else {
            // Other sizes unlikely but attempt to read as float64 on subset
            durationValue = view.getFloat64(dataOffset + (dataSize - 8), false);
          }
        } catch (_) {}
      }
    }
    // TimecodeScale ID 0x2A 0xD7 0xB1
    if (b0 === 0x2A && view.getUint8(i + 1) === 0xD7 && view.getUint8(i + 2) === 0xB1) {
      const { value: dataSize, length: sizeLen } = readVint(view, i + 3);
      if (dataSize > 0 && sizeLen > 0) {
        const dataOffset = i + 3 + sizeLen;
        try {
          // Read unsigned integer, big-endian
          let val = 0;
          for (let j = 0; j < dataSize; j++) {
            val = (val << 8) + view.getUint8(dataOffset + j);
          }
          if (val > 0) timecodeScale = val;
        } catch (_) {}
      }
    }
    // Early exit if we found both
    if (isFinite(durationValue)) {
      // Keep looping in case TimecodeScale appears later, but break if both found
      // If we already found TimecodeScale earlier as default or parsed, still okay
      // Heuristic: if both are found by now (durationValue finite and timecodeScale set), break after small lookahead
      // but to keep it simple, break immediately if both seen explicitly isn't tracked; acceptable for performance.
    }
  }
  return { durationValue, timecodeScale };
}

function readVint(view, offset) {
  if (offset >= view.byteLength) return { value: 0, length: 0 };
  const first = view.getUint8(offset);
  // Determine length from leading 1 bit position
  let mask = 0x80;
  let length = 1;
  while (length <= 8 && (first & mask) === 0) {
    mask >>= 1;
    length++;
  }
  if (length > 8) return { value: 0, length: 0 };
  // Read value: clear the marker bit
  let value = first & (~mask);
  for (let i = 1; i < length; i++) {
    value = (value << 8) + view.getUint8(offset + i);
  }
  return { value, length };
}
